import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Grid, IconButton, MenuItem, Stack, Switch,
  TextField, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { practice } from '@/api/endpoints';
import { formatPrice } from '@/utils/format';
import type { CarePackage, MedicalProcedure } from '@/types/medical';

const ITEM_KINDS = [
  { code: 'procedure',  label: 'Procedure' },
  { code: 'lab_panel',  label: 'Lab panel' },
  { code: 'follow_up',  label: 'Follow-up visit' },
];

export function PracticeCarePackagesPage() {
  const q = useApiQuery({
    queryKey: ['practice', 'care-packages'],
    queryFn:  practice.carePackages.list as () => Promise<CarePackage[]>,
  });

  const [editing, setEditing] = useState<CarePackage | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHeader
        title="Care packages"
        subtitle="Bundle visits, lab panels and follow-ups at a fixed price."
        actions={
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreating(true)}>
            New package
          </Button>
        }
      />
      <DataState
        query={q}
        empty={{
          title: 'No care packages yet',
          description: 'Bundle 5 visits + a lab panel for diabetics, post-op recovery, antenatal — anything where a fixed price helps the patient.',
        }}
      >
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((p) => (
              <Row key={p.id} pkg={p} onEdit={() => setEditing(p)} />
            ))}
          </Stack>
        )}
      </DataState>

      {(creating || editing) && (
        <PackageDialog
          pkg={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
        />
      )}
    </>
  );
}

function Row({ pkg, onEdit }: { pkg: CarePackage; onEdit: () => void }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Inventory2Icon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {pkg.name}
              </Typography>
              {!pkg.is_active && <Chip size="small" label="inactive" variant="outlined" />}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {(pkg.included_items ?? []).length} item(s) ·{' '}
              <span className="tabular">{pkg.validity_days}-day validity</span>
            </Typography>
          </Box>
          <Typography variant="subtitle1" className="tabular" fontWeight={600}>
            {formatPrice(pkg.price_amount, pkg.price_currency)}
          </Typography>
          <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>Edit</Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

type DraftItem = { kind: string; ref_id: string; count: number };

function PackageDialog({
  pkg, onClose,
}: { pkg: CarePackage | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = pkg !== null;

  const proceduresQ = useApiQuery({
    queryKey: ['practice', 'procedures'],
    queryFn:  practice.procedures.list as () => Promise<MedicalProcedure[]>,
  });

  const [name, setName] = useState(pkg?.name ?? '');
  const [description, setDescription] = useState(pkg?.description ?? '');
  const [price, setPrice] = useState(pkg?.price_amount ?? '');
  const [currency, setCurrency] = useState(pkg?.price_currency ?? 'EUR');
  const [validityDays, setValidityDays] = useState(String(pkg?.validity_days ?? 365));
  const [isActive, setIsActive] = useState(pkg?.is_active ?? true);
  const [items, setItems] = useState<DraftItem[]>(
    (pkg?.included_items ?? []).map((it) => ({
      kind: it.kind,
      ref_id: it.procedure_id != null ? String(it.procedure_id)
            : it.panel_code ?? '',
      count: it.count,
    }))
  );

  useEffect(() => {
    setName(pkg?.name ?? '');
    setDescription(pkg?.description ?? '');
    setPrice(pkg?.price_amount ?? '');
    setCurrency(pkg?.price_currency ?? 'EUR');
    setValidityDays(String(pkg?.validity_days ?? 365));
    setIsActive(pkg?.is_active ?? true);
  }, [pkg?.id]);

  const saveMut = useApiMutation({
    mutationFn: () => {
      const payload = {
        name, description,
        price_amount: price,
        price_currency: currency,
        validity_days: parseInt(validityDays, 10) || 365,
        is_active: isActive,
        included_items: items.map((it) => {
          const out: Record<string, unknown> = { kind: it.kind, count: it.count };
          if (it.kind === 'procedure' || it.kind === 'follow_up') {
            out.procedure_id = parseInt(it.ref_id, 10);
          } else if (it.kind === 'lab_panel') {
            out.panel_code = it.ref_id;
          }
          return out;
        }),
      };
      return isEdit
        ? practice.carePackages.update(pkg!.id, payload)
        : practice.carePackages.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Package saved.' : 'Package created.');
      qc.invalidateQueries({ queryKey: ['practice', 'care-packages'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const updateItem = (i: number, patch: Partial<DraftItem>) =>
    setItems(items.map((it, j) => j === i ? { ...it, ...patch } : it));
  const addItem = () =>
    setItems([...items, { kind: 'procedure', ref_id: '', count: 1 }]);
  const removeItem = (i: number) => setItems(items.filter((_, j) => j !== i));

  const valid = name.trim().length > 1 && parseFloat(price || '0') > 0
    && items.length > 0 && items.every((it) => it.ref_id && it.count > 0);

  return (
    <Dialog open onClose={() => !saveMut.isPending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit care package' : 'New care package'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField fullWidth required label="Name"
                       value={name} onChange={(e) => setName(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label="Description"
                       value={description}
                       onChange={(e) => setDescription(e.target.value)} />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField fullWidth required label="Price"
                       value={price} onChange={(e) => setPrice(e.target.value)}
                       inputMode="decimal" />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField fullWidth select label="Currency"
                       value={currency}
                       onChange={(e) => setCurrency(e.target.value)}>
              {['EUR', 'USD', 'PLN', 'GBP'].map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Validity (days)" type="number"
                       value={validityDays}
                       onChange={(e) => setValidityDays(e.target.value)}
                       inputProps={{ min: 1 }} />
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" justifyContent="space-between" alignItems="center"
                   sx={{ mb: 1.5 }}>
              <Typography variant="overline" color="text.secondary">
                Included items
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addItem}>
                Add item
              </Button>
            </Stack>
            <Stack spacing={1.5}>
              {items.map((it, i) => (
                <Card key={i} variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <TextField size="small" select label="Kind"
                                 value={it.kind}
                                 onChange={(e) => updateItem(i, { kind: e.target.value })}
                                 sx={{ minWidth: 140 }}>
                        {ITEM_KINDS.map((k) => (
                          <MenuItem key={k.code} value={k.code}>{k.label}</MenuItem>
                        ))}
                      </TextField>
                      {(it.kind === 'procedure' || it.kind === 'follow_up') ? (
                        <TextField size="small" select label="Procedure" sx={{ flexGrow: 1 }}
                                   value={it.ref_id}
                                   onChange={(e) => updateItem(i, { ref_id: e.target.value })}>
                          {(proceduresQ.data ?? [])
                            .filter((p) => p.is_active)
                            .map((p) => (
                              <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
                            ))}
                        </TextField>
                      ) : (
                        <TextField size="small" label="Lab panel code" sx={{ flexGrow: 1 }}
                                   value={it.ref_id}
                                   onChange={(e) => updateItem(i, { ref_id: e.target.value })}
                                   placeholder="HBA1C, LIPID, etc." />
                      )}
                      <TextField size="small" label="Count" type="number" sx={{ width: 90 }}
                                 value={it.count}
                                 onChange={(e) => updateItem(i, { count: parseInt(e.target.value || '1', 10) })} />
                      <IconButton size="small" color="error"
                                  onClick={() => removeItem(i)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={isActive}
                               onChange={(_, v) => setIsActive(v)} />}
              label="Active (purchasable)" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveMut.isPending}>Cancel</Button>
        <Button variant="contained"
                disabled={!valid || saveMut.isPending}
                onClick={() => saveMut.mutate()}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
